import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function EventApiConfig({ token, selectedProperty, userScopes, accessibleProperties }) {
  const { isMobile } = useIsMobile();
  const [vendors, setVendors] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Editing state per vendor
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState({
    vendor_name: '', api_key: '', api_base_url: '', location_query: '',
    radius_km: 50, event_categories: '', sync_frequency: 'WEEKLY', is_enabled: false,
  });
  const [showKey, setShowKey] = useState(false);

  // City list state
  const [cities, setCities] = useState([]);
  const [cityName, setCityName] = useState('');
  const [cityState, setCityState] = useState('');
  const [cityCountry, setCityCountry] = useState('IN');
  const [citySaving, setCitySaving] = useState(false);

  // Global events browser state
  const [globalEvents, setGlobalEvents] = useState([]);
  const [globalCities, setGlobalCities] = useState([]);
  const [searchCity, setSearchCity] = useState('');
  const [globalLoading, setGlobalLoading] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState(new Set());
  const [syncingToProperty, setSyncingToProperty] = useState(false);

  // PredictHQ Insights state
  const [insights, setInsights] = useState(null);
  const [insightsCity, setInsightsCity] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsExpanded, setInsightsExpanded] = useState(null); // expanded event id

  const chainId = selectedProperty?.chain_id || userScopes?.[0]?.chain_id || accessibleProperties?.[0]?.chain_id || '';

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': chainId,
    'x-property-id': selectedProperty?.id || 'GLOBAL',
  }), [token, chainId, selectedProperty]);

  const loadVendors = async () => {
    try {
      const res = await fetch('/api/v1/event-calendar/vendors', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setVendors(await res.json());
    } catch {}
  };

  const loadConfigs = async () => {
    if (!chainId) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/event-calendar/config', { headers: headers() });
      if (res.ok) setConfigs(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  const loadCities = async () => {
    if (!chainId) return;
    try {
      const res = await fetch('/api/v1/event-calendar/cities', { headers: headers() });
      if (res.ok) setCities(await res.json());
    } catch {}
  };

  const addCity = async () => {
    if (!cityName.trim()) return setError('City name is required');
    setCitySaving(true); setError('');
    try {
      const res = await fetch('/api/v1/event-calendar/cities', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ city_name: cityName.trim(), state_name: cityState.trim() || null, country_code: cityCountry || 'IN' }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to add city');
      setCityName(''); setCityState('');
      loadCities();
    } catch (err) { setError(err.message); }
    finally { setCitySaving(false); }
  };

  const removeCity = async (id) => {
    try {
      await fetch(`/api/v1/event-calendar/cities/${id}`, { method: 'DELETE', headers: headers() });
      loadCities();
    } catch {}
  };

  const searchGlobalEvents = async (city) => {
    setGlobalLoading(true); setSelectedEventIds(new Set());
    try {
      const q = new URLSearchParams();
      if (city) q.set('city', city);
      q.set('from', new Date().toISOString().slice(0, 10));
      const res = await fetch(`/api/v1/event-calendar/global?${q.toString()}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setGlobalEvents(data.events || []);
        setGlobalCities(data.available_cities || []);
      }
    } catch {}
    finally { setGlobalLoading(false); }
  };

  const toggleEventSelect = (id) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedEventIds.size === globalEvents.length) setSelectedEventIds(new Set());
    else setSelectedEventIds(new Set(globalEvents.map(e => e.id)));
  };

  const syncSelectedToProperty = async () => {
    if (!selectedProperty?.id) return setError('Select a property first to sync events');
    if (!selectedEventIds.size) return setError('Select at least one event');
    setSyncingToProperty(true); setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/event-calendar/global/sync-to-property', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ event_ids: Array.from(selectedEventIds) }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      setMsg(`Synced to property: ${data.inserted} new events, ${data.skipped} already existed`);
      setSelectedEventIds(new Set());
    } catch (err) { setError(err.message); }
    finally { setSyncingToProperty(false); }
  };

  const loadInsights = async (city) => {
    setInsightsLoading(true);
    try {
      const q = new URLSearchParams();
      if (city) q.set('city', city);
      q.set('from', new Date().toISOString().slice(0, 10));
      const res = await fetch(`/api/v1/event-calendar/insights?${q.toString()}`, { headers: headers() });
      if (res.ok) setInsights(await res.json());
    } catch {}
    finally { setInsightsLoading(false); }
  };

  useEffect(() => { loadVendors(); }, [token]);
  useEffect(() => { if (chainId) { loadConfigs(); loadCities(); searchGlobalEvents(''); } }, [chainId]);

  const startEdit = (vendor) => {
    const existing = configs.find(c => c.vendor_name === vendor.id);
    setEditVendor(vendor.id);
    setForm({
      vendor_name: vendor.id,
      api_key: existing?.api_key || '',
      api_base_url: existing?.api_base_url || vendor.base_url || '',
      location_query: existing?.location_query || '',
      radius_km: existing?.radius_km || 50,
      event_categories: existing?.event_categories || '',
      sync_frequency: existing?.sync_frequency || 'WEEKLY',
      is_enabled: existing?.is_enabled || false,
    });
    setShowKey(false);
  };

  const saveConfig = async () => {
    setSaving(true); setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/event-calendar/config', {
        method: 'POST', headers: headers(), body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMsg(`${form.vendor_name} configuration saved`);
      setEditVendor(null);
      loadConfigs();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const syncVendor = async (vendorName) => {
    setSyncing(prev => ({ ...prev, [vendorName]: true }));
    setError(''); setMsg('');
    try {
      const res = await fetch(`/api/v1/event-calendar/sync/${vendorName}`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ months: 3 }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      setMsg(`${vendorName}: Fetched ${data.fetched} events, ${data.inserted} new, ${data.skipped} skipped`);
      loadConfigs();
    } catch (err) { setError(err.message); }
    finally { setSyncing(prev => ({ ...prev, [vendorName]: false })); }
  };

  const syncAll = async () => {
    setSyncing(prev => ({ ...prev, ALL: true }));
    setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/event-calendar/sync', {
        method: 'POST', headers: headers(), body: JSON.stringify({ months: 3 }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      const summary = (data.details || []).map(d => `${d.vendor}: ${d.status === 'ok' ? `${d.inserted} new` : d.error}`).join(', ');
      setMsg(`Synced ${data.synced_vendors}/${data.total_vendors} vendors — ${summary}`);
      loadConfigs();
    } catch (err) { setError(err.message); }
    finally { setSyncing(prev => ({ ...prev, ALL: false })); }
  };

  const deleteConfig = async (vendorName) => {
    if (!window.confirm(`Remove ${vendorName} configuration?`)) return;
    try {
      await fetch(`/api/v1/event-calendar/config/${vendorName}`, { method: 'DELETE', headers: headers() });
      loadConfigs();
      setMsg(`${vendorName} removed`);
    } catch {}
  };

  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const sectionStyle = { marginBottom: '1.5rem', padding: '1rem 1.2rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' };

  if (!chainId) return <div className="page-shell"><p style={{ color: '#94a3b8' }}>Unable to determine chain. Please log in again.</p></div>;

  return (
    <div className="page-shell">
      <h3 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.3rem' }}>Event Calendar API Configuration</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
        Configure external event calendar APIs to auto-fetch local events (festivals, conferences, holidays) for demand forecasting.
      </p>
      <div style={{ padding: '0.4rem 0.8rem', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '0.78rem', color: '#1e40af', marginBottom: '1.5rem', display: 'inline-block' }}>
        Global Setting — events synced for all properties
      </div>

      {loading && <div style={{ color: '#6b7280', marginBottom: '1rem' }}>Loading...</div>}
      {msg && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '0.85rem', marginBottom: '1rem' }}>{msg}</div>}
      {error && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</div>}

      {/* Sync All button */}
      {configs.some(c => c.is_enabled) && cities.length > 0 && (
        <button onClick={syncAll} disabled={syncing.ALL}
          style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 8, cursor: syncing.ALL ? 'wait' : 'pointer', fontWeight: 700, background: '#059669', color: '#fff', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
          {syncing.ALL ? 'Syncing All...' : `Sync All Vendors (${cities.length} cities)`}
        </button>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 300px', gap: '1.5rem', alignItems: 'start' }}>

      {/* LEFT: Vendor Cards */}
      <div>
      {vendors.map(vendor => {
        const cfg = configs.find(c => c.vendor_name === vendor.id);
        const isEditing = editVendor === vendor.id;
        const statusColor = vendor.status === 'supported' ? '#059669' : '#d97706';

        return (
          <div key={vendor.id} style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--theme-primary-dark)' }}>{vendor.name}</span>
                <span style={{ marginLeft: 8, fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                  background: vendor.status === 'supported' ? '#dcfce7' : '#fef3c7',
                  color: statusColor }}>
                  {vendor.status === 'supported' ? 'Supported' : 'Coming Soon'}
                </span>
                {cfg?.is_enabled && <span style={{ marginLeft: 8, fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: '#dcfce7', color: '#059669' }}>Enabled</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                {cfg?.last_synced_at && (
                  <span style={{ fontSize: '0.72rem', color: '#6b7280', marginRight: 4 }}>
                    Last sync: {new Date(cfg.last_synced_at).toLocaleString()}
                  </span>
                )}
                {vendor.status === 'supported' && (
                  <>
                    {cfg?.is_enabled && (
                      <button onClick={() => syncVendor(vendor.id)} disabled={syncing[vendor.id]}
                        style={{ padding: '0.3rem 0.7rem', border: 'none', borderRadius: 6, cursor: syncing[vendor.id] ? 'wait' : 'pointer', fontWeight: 600, background: '#059669', color: '#fff', fontSize: '0.78rem' }}>
                        {syncing[vendor.id] ? 'Syncing...' : 'Sync Now'}
                      </button>
                    )}
                    <button onClick={() => isEditing ? setEditVendor(null) : startEdit(vendor)}
                      style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontWeight: 600, background: '#fff', fontSize: '0.78rem' }}>
                      {isEditing ? 'Cancel' : 'Configure'}
                    </button>
                    {cfg && (
                      <button onClick={() => deleteConfig(vendor.id)}
                        style={{ padding: '0.3rem 0.7rem', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontWeight: 600, background: '#fef2f2', color: '#991b1b', fontSize: '0.78rem' }}>
                        Remove
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Base URL: <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3 }}>{vendor.base_url}</code>
              {' · '}<a href={vendor.docs} target="_blank" rel="noopener noreferrer" style={{ color: '#1e40af' }}>API Docs</a>
            </div>

            {/* Edit Form */}
            {isEditing && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>API Key</label>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <input type={showKey ? 'text' : 'password'} value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}
                        placeholder="Bearer token / API key" style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={() => setShowKey(!showKey)} type="button"
                        style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: '0.75rem' }}>
                        {showKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>API Base URL</label>
                    <input value={form.api_base_url} onChange={e => setForm({ ...form, api_base_url: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Location / City</label>
                    <input value={form.location_query} onChange={e => setForm({ ...form, location_query: e.target.value })}
                      placeholder="e.g. Chennai, Tamil Nadu" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Radius (km)</label>
                    <input type="number" value={form.radius_km} onChange={e => setForm({ ...form, radius_km: Number(e.target.value) })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Event Categories (comma-separated)</label>
                    <input value={form.event_categories} onChange={e => setForm({ ...form, event_categories: e.target.value })}
                      placeholder="festival, conference, sports, concert" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Sync Frequency</label>
                    <select value={form.sync_frequency} onChange={e => setForm({ ...form, sync_frequency: e.target.value })} style={inputStyle}>
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MANUAL">Manual Only</option>
                    </select>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: '0.75rem' }}>
                  <input type="checkbox" checked={form.is_enabled} onChange={e => setForm({ ...form, is_enabled: e.target.checked })} style={{ width: 18, height: 18 }} />
                  <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>Enable this vendor</span>
                </label>
                <button onClick={saveConfig} disabled={saving}
                  style={{ marginTop: '0.75rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: 8, background: saving ? '#94a3b8' : 'var(--theme-primary-dark)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.88rem' }}>
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* RIGHT: City List */}
      <div style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', padding: '1rem 1.2rem', position: 'sticky', top: '1rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--theme-primary-dark)', marginBottom: '0.5rem' }}>Configured Cities</div>
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          Events will be fetched for these cities from all enabled vendors.
        </p>

        {/* Add city form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
          <input value={cityName} onChange={e => setCityName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCity()}
            placeholder="City name (e.g. Chennai)"
            style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <input value={cityState} onChange={e => setCityState(e.target.value)}
              placeholder="State (optional)"
              style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem', boxSizing: 'border-box' }} />
            <select value={cityCountry} onChange={e => setCityCountry(e.target.value)}
              style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem' }}>
              <option value="IN">IN</option>
              <option value="US">US</option>
              <option value="UK">UK</option>
              <option value="AE">AE</option>
              <option value="SG">SG</option>
            </select>
          </div>
          <button onClick={addCity} disabled={citySaving}
            style={{ width: '100%', padding: '0.45rem', border: 'none', borderRadius: 6, cursor: citySaving ? 'wait' : 'pointer',
              fontWeight: 600, background: '#059669', color: '#fff', fontSize: '0.85rem' }}>
            {citySaving ? 'Adding...' : '+ Add City'}
          </button>
        </div>

        {/* City list */}
        {cities.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>
            No cities configured yet. Add your first city above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {cities.map(city => (
              <div key={city.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.4rem 0.6rem', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{city.city_name}</div>
                  {(city.state_name || city.country_code) && (
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                      {[city.state_name, city.country_code].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                <button onClick={() => removeCity(city.id)}
                  style={{ border: 'none', background: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, padding: '0 0.3rem' }}
                  title="Remove city">x</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: '#94a3b8' }}>
          {cities.length} cities configured. Sync fetches events for all cities from all enabled vendors.
        </div>
      </div>

      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* GLOBAL EVENTS BROWSER */}
      {/* ════════════════════════════════════════════════ */}
      <div style={{ marginTop: '2rem', padding: '1.2rem', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#059669', marginBottom: '0.3rem' }}>Global Event Calendar</div>
        <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          Browse events fetched from all vendors. Search by city, select events, and sync them to your property.
        </p>

        {/* Search bar */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={searchCity} onChange={e => { setSearchCity(e.target.value); searchGlobalEvents(e.target.value); }}
            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', minWidth: 200 }}>
            <option value="">All Cities</option>
            {globalCities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => searchGlobalEvents(searchCity)} disabled={globalLoading}
            style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, background: '#059669', color: '#fff', fontSize: '0.82rem' }}>
            {globalLoading ? 'Searching...' : 'Refresh'}
          </button>
          {selectedEventIds.size > 0 && selectedProperty?.id && (
            <button onClick={syncSelectedToProperty} disabled={syncingToProperty}
              style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: syncingToProperty ? 'wait' : 'pointer',
                fontWeight: 700, background: '#1e40af', color: '#fff', fontSize: '0.82rem', marginLeft: 'auto' }}>
              {syncingToProperty ? 'Syncing...' : `Sync ${selectedEventIds.size} Event(s) to Property`}
            </button>
          )}
          {selectedEventIds.size > 0 && !selectedProperty?.id && (
            <span style={{ fontSize: '0.78rem', color: '#dc2626', marginLeft: 'auto' }}>Select a property first to sync events</span>
          )}
        </div>

        {/* Events table */}
        {globalEvents.length > 0 ? (
          <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#059669', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', width: 30 }}>
                    <input type="checkbox" checked={selectedEventIds.size === globalEvents.length && globalEvents.length > 0}
                      onChange={selectAll} style={{ cursor: 'pointer' }} />
                  </th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Event</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>City</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Impact</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                </tr>
              </thead>
              <tbody>
                {globalEvents.map((evt, i) => (
                  <tr key={evt.id} style={{ borderBottom: '1px solid #f3f4f6', background: selectedEventIds.has(evt.id) ? '#eff6ff' : (i % 2 === 0 ? '#fff' : '#fafafa') }}>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <input type="checkbox" checked={selectedEventIds.has(evt.id)}
                        onChange={() => toggleEventSelect(evt.id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{evt.event_name}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{evt.city_name}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                        background: evt.event_type === 'FESTIVAL' ? '#fef3c7' : evt.event_type === 'CONFERENCE' ? '#dbeafe' : evt.event_type === 'CONCERT' ? '#fce7f3' : '#f3f4f6',
                        color: evt.event_type === 'FESTIVAL' ? '#92400e' : evt.event_type === 'CONFERENCE' ? '#1e40af' : evt.event_type === 'CONCERT' ? '#9d174d' : '#475569' }}>
                        {evt.event_type}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` — ${evt.end_date}` : ''}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700,
                        color: evt.expected_impact === 'HIGH' ? '#dc2626' : evt.expected_impact === 'MEDIUM' ? '#d97706' : '#6b7280' }}>
                        {evt.expected_impact}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.72rem', color: '#6b7280' }}>{evt.vendor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            {globalLoading ? 'Loading events...' : 'No global events found. Configure vendors and sync first.'}
          </div>
        )}

        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
          {globalEvents.length} events found{searchCity ? ` in ${searchCity}` : ''}.
          {selectedEventIds.size > 0 && ` ${selectedEventIds.size} selected.`}
        </div>
      </div>

      {/* ════════════════════════════════════════════════ */}
      {/* PREDICTHQ AI INSIGHTS */}
      {/* ════════════════════════════════════════════════ */}
      <div style={{ marginTop: '2rem', padding: '1.2rem', background: '#faf5ff', borderRadius: 10, border: '1px solid #d8b4fe' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#7c3aed' }}>PredictHQ AI Insights</div>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0.2rem 0 0' }}>
              AI/ML predictions — event rank, predicted attendance, accommodation &amp; hospitality spend, demand impact patterns.
            </p>
          </div>
          <button onClick={() => loadInsights(insightsCity)} disabled={insightsLoading}
            style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: insightsLoading ? 'wait' : 'pointer',
              fontWeight: 700, background: '#7c3aed', color: '#fff', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            {insightsLoading ? 'Loading...' : insights ? 'Refresh Insights' : 'Load AI Insights'}
          </button>
        </div>

        {/* Summary Cards */}
        {insights?.summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
            {[
              { label: 'Total Events', value: insights.summary.total_events, color: '#7c3aed' },
              { label: 'High Impact', value: insights.summary.high_impact_events, color: '#dc2626' },
              { label: 'Medium Impact', value: insights.summary.medium_impact_events, color: '#d97706' },
              { label: 'Total Attendance', value: (insights.summary.total_predicted_attendance || 0).toLocaleString(), color: '#059669' },
              { label: 'Accommodation Spend', value: `$${(insights.summary.total_accommodation_spend_usd || 0).toLocaleString()}`, color: '#1e40af' },
              { label: 'Hospitality Spend', value: `$${(insights.summary.total_hospitality_spend_usd || 0).toLocaleString()}`, color: '#9333ea' },
            ].map((card, i) => (
              <div key={i} style={{ padding: '0.6rem', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600, marginBottom: 2 }}>{card.label}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: card.color }}>{card.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Insights Table */}
        {insights?.events?.length > 0 && (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #d8b4fe', borderRadius: 8, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#7c3aed', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Event</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>PHQ Rank</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Local Rank</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Attendance</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Accom. Spend</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Hosp. Spend</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {insights.events.map((evt, i) => {
                  const rank = evt.phq_rank || 0;
                  const rankColor = rank >= 70 ? '#dc2626' : rank >= 40 ? '#d97706' : '#6b7280';
                  const isExpanded = insightsExpanded === evt.id;
                  return (
                    <React.Fragment key={evt.id}>
                      <tr style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#faf5ff' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <div style={{ fontWeight: 600 }}>{evt.title}</div>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{evt.category} · {evt.city_name}</div>
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: rankColor }}>{rank}</span>
                          <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>/100</span>
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                          {evt.local_rank ? <span style={{ fontWeight: 700, color: '#475569' }}>{evt.local_rank}</span> : <span style={{ color: '#d1d5db' }}>-</span>}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>
                          {evt.phq_attendance ? Number(evt.phq_attendance).toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', color: '#1e40af', fontWeight: 600 }}>
                          {evt.predicted_spend_accommodation ? `$${Number(evt.predicted_spend_accommodation).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', color: '#9333ea', fontWeight: 600 }}>
                          {evt.predicted_spend_hospitality ? `$${Number(evt.predicted_spend_hospitality).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '0.4rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` — ${evt.end_date}` : ''}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                          <button onClick={() => setInsightsExpanded(isExpanded ? null : evt.id)}
                            style={{ padding: '0.2rem 0.5rem', border: '1px solid #d8b4fe', borderRadius: 4, cursor: 'pointer',
                              fontSize: '0.7rem', fontWeight: 600, background: isExpanded ? '#7c3aed' : '#fff', color: isExpanded ? '#fff' : '#7c3aed' }}>
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ background: '#f5f3ff' }}>
                          <td colSpan={8} style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', fontSize: '0.78rem' }}>
                              {/* Spend Breakdown */}
                              <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: '0.3rem' }}>Predicted Spend (USD)</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total:</span><b>${Number(evt.predicted_spend_total || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Accommodation:</span><b style={{ color: '#1e40af' }}>${Number(evt.predicted_spend_accommodation || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Hospitality (F&B):</span><b style={{ color: '#9333ea' }}>${Number(evt.predicted_spend_hospitality || 0).toLocaleString()}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Transportation:</span><b>${Number(evt.predicted_spend_transportation || 0).toLocaleString()}</b></div>
                              </div>

                              {/* Labels & Classification */}
                              <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.3rem' }}>AI Labels</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                  {(Array.isArray(evt.phq_labels) ? evt.phq_labels : []).map((lbl, li) => (
                                    <span key={li} style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
                                      {lbl.label || lbl} {lbl.weight ? `(${(lbl.weight * 100).toFixed(0)}%)` : ''}
                                    </span>
                                  ))}
                                  {(Array.isArray(evt.labels) ? evt.labels : []).map((lbl, li) => (
                                    <span key={`l${li}`} style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#f3f4f6', color: '#475569' }}>
                                      {lbl}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Location */}
                              <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontWeight: 700, color: '#d97706', marginBottom: '0.3rem' }}>Location</div>
                                {evt.formatted_address && <div>{evt.formatted_address}</div>}
                                {evt.latitude && evt.longitude && <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>Lat: {evt.latitude}, Lng: {evt.longitude}</div>}
                                <div style={{ marginTop: '0.2rem' }}>Country: <b>{evt.country_code}</b></div>
                                {evt.event_state && <div>State: <b>{evt.event_state}</b></div>}
                                {evt.parent_event_id && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Parent: {evt.parent_event_id}</div>}
                              </div>

                              {/* Demand Impact Patterns */}
                              {(evt.impact_pattern_accommodation || evt.impact_pattern_hospitality) && (
                                <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                  <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '0.3rem' }}>Demand Impact Patterns</div>
                                  {evt.impact_pattern_accommodation?.impacts && (
                                    <div style={{ marginBottom: '0.4rem' }}>
                                      <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#1e40af' }}>Accommodation:</div>
                                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                                        {evt.impact_pattern_accommodation.impacts.map((imp, ii) => (
                                          <span key={ii} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                                            background: imp.position === 'event_day' ? '#fee2e2' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                            color: imp.position === 'event_day' ? '#991b1b' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                            {imp.date_local}: {imp.value > 0 ? '+' : ''}{imp.value} ({imp.position})
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {evt.impact_pattern_hospitality?.impacts && (
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#9333ea' }}>Hospitality (F&B):</div>
                                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                                        {evt.impact_pattern_hospitality.impacts.map((imp, ii) => (
                                          <span key={ii} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                                            background: imp.position === 'event_day' ? '#fce7f3' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                            color: imp.position === 'event_day' ? '#9d174d' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                            {imp.date_local}: {imp.value > 0 ? '+' : ''}{imp.value} ({imp.position})
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Entities */}
                              {Array.isArray(evt.entities) && evt.entities.length > 0 && (
                                <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Linked Entities</div>
                                  {evt.entities.map((ent, ei) => (
                                    <div key={ei} style={{ fontSize: '0.75rem', marginBottom: '0.2rem' }}>
                                      <span style={{ fontWeight: 600 }}>{ent.name}</span>
                                      <span style={{ marginLeft: 6, fontSize: '0.68rem', padding: '1px 5px', borderRadius: 3, background: '#f3f4f6', color: '#6b7280' }}>{ent.type}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {insights && !insights.events?.length && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            No PredictHQ insights found. Sync PredictHQ first.
          </div>
        )}

        {!insights && !insightsLoading && (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
            Click "Load AI Insights" to view PredictHQ predictions — event rankings, attendance forecasts, accommodation spend, and demand impact patterns.
          </div>
        )}
      </div>
    </div>
  );
}

export default EventApiConfig;
